import { useState } from 'react';
import { F, R, withAlpha, DUR } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';

/**
 * InfoBanner — flache, editoriale Info-Karte (red. „neues Design").
 *
 * Flaches App-Surface + 1px-Hairline + scharfe 3px-Ecken (wie SummaryCard /
 * Markets-Cards), KEIN Drop-Shadow, KEIN flächiger Tint. Die Tonalität
 * (info/success/warning/danger) kommt ausschließlich über:
 *  - einen 3px-Akzentstreifen links (inset, läuft mit der Rundung mit)
 *  - ein dezentes Outline-Icon im Akzent-Farbton (kein Container)
 * Damit bleibt es zwischen Light/Dark konsistent und editorial-ruhig.
 *
 * Props:
 *  - tone:     'info' | 'success' | 'warning' | 'danger' (alias 'error') (default 'info')
 *  - title:    Bold-Titel-Zeile
 *  - children: Beschreibungs-Body (Text oder JSX)
 *  - icon:     optional, JSX (SVG); fällt sonst auf Tone-Default zurück
 *  - onClick:  optional, macht den Banner klickbar (z.B. CTA-Vorschlag)
 *  - cta:      optional, kurzer Text rechts neben Title (z.B. "→ Umstellen")
 *  - compact:  bool, reduziert Padding für inline-Listen
 */
export default function InfoBanner({
  tone = 'info',
  title,
  children,
  icon,
  onClick,
  cta,
  compact = false,
  style,
}) {
  const { T } = useTheme();
  const [hover, setHover] = useState(false);

  // Tone → Akzentfarbe. Markentreu/minimal: info = NEUTRAL (kein off-brand Blau),
  // warning = Brand-Gold, danger = Brand-Rot. Tonalität nur übers dezente Icon —
  // kein farbiger Streifen/Container mehr (feedback_no_pills_borders_glow).
  const TONE_COLOR = {
    info:    T.sub,
    success: T.green,
    warning: T.gold || '#F5C542',
    danger:  T.red,
    error:   T.red,
  };
  const accent = TONE_COLOR[tone] || TONE_COLOR.info;

  // Default-Icons (Lucide-Style, 18×18)
  const DEFAULT_ICONS = {
    info:    <><circle cx="12" cy="12" r="9.5"/><path d="M12 8v.01M12 11v5"/></>,
    success: <><circle cx="12" cy="12" r="9.5"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/></>,
    warning: <><path d="M10.3 3.7l-8.1 14a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3l-8.1-14a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 16v.01"/></>,
    danger:  <><circle cx="12" cy="12" r="9.5"/><path d="M9 9l6 6M15 9l-6 6"/></>,
    error:   <><circle cx="12" cy="12" r="9.5"/><path d="M9 9l6 6M15 9l-6 6"/></>,
  };

  const renderedIcon = icon != null ? icon : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {DEFAULT_ICONS[tone] || DEFAULT_ICONS.info}
    </svg>
  );

  const Comp = onClick ? 'button' : 'div';
  const interactive = !!onClick;

  return (
    <Comp
      onClick={onClick}
      onMouseEnter={interactive ? () => setHover(true) : undefined}
      onMouseLeave={interactive ? () => setHover(false) : undefined}
      style={{
        // Layout
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: compact ? '11px 15px 11px 16px' : '15px 16px 15px 17px',
        width: '100%',
        textAlign: 'left',
        // Cleane Surface — KEIN farbiger Streifen mehr; nur ein subtiler
        // Inset-Highlight für etwas Tiefe (kein Drop-Shadow/Glow).
        background: interactive && hover ? (T.itemHover || T.surfaceHi || T.surface) : T.surface,
        boxShadow: T.cardInset,
        border: `1px solid ${interactive && hover ? T.text : (T.line || T.border)}`,
        borderRadius: R.md,
        // Button-Reset (falls onClick)
        cursor: onClick ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        color: T.text, fontFamily: F.family,
        transition: `border-color ${DUR.fast}ms ease, background ${DUR.fast}ms ease`,
        ...style,
      }}
    >
      {/* Icon — farbiger Glyph, kein Container */}
      <div style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 22, minWidth: 22, height: 22,
        color: accent,
        marginTop: 0.5,
      }}>
        {renderedIcon}
      </div>

      {/* Text-Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8,
            fontSize: 13.5, fontWeight: 700,
            letterSpacing: '-0.01em', color: T.text,
            marginBottom: children ? 4 : 0,
          }}>
            <span style={{ flex: 1, minWidth: 0 }}>{title}</span>
            {cta && (
              <span style={{
                flexShrink: 0, fontSize: 12.5, fontWeight: 700,
                color: accent, letterSpacing: '0.01em',
              }}>{cta}</span>
            )}
          </div>
        )}
        {children && (
          <div style={{
            fontSize: 12.5, color: T.sub, lineHeight: 1.55, fontWeight: 500,
          }}>
            {children}
          </div>
        )}
      </div>

      {/* Chevron, wenn klickbar */}
      {onClick && !cta && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke={withAlpha(T.sub, 0.6)} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 3 }}
        >
          <path d="M9 18l6-6-6-6"/>
        </svg>
      )}
    </Comp>
  );
}
